import {
  Box,
  Button,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  CircularProgress,
  TextField,
} from "@mui/material";
import { Delete as DeleteIcon, Check, Close } from "@mui/icons-material";
import ToggleButtonComponent from "../../../shared/Dashboard/ToogleButton";
import { useState } from "react";
import axiosInstance from "../../../utils/axios";
import { Pencil } from "lucide-react";
import { isReadOnly } from "../../../utils/permissionUtils";
import { sortLaydownsByNameAsc } from "../../../shared/Helper";

const LaydownList = ({
  currentLaydown,
  setCurrentLaydown,
  laydownList,
  isEditMode = false,
  active,
  setActive,
  setLaydown,
  setCurrentLaydowns,
  project_id
}) => {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteLoader, setDeleteLoader] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [editLaydownId, setEditLaydownId] = useState(null);
  const [editLaydownName, setEditLaydownName] = useState("");
  const [editLoader, setEditLoader] = useState(false);

  const handleLaydownClick = (item) => {
    if (!editLaydownId) {
      setCurrentLaydown(item);
      setCurrentLaydowns(item);
    }
  };

  const handleDeleteClick = (e, item) => {
    e.stopPropagation();
    setDeleteTarget(item);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setDeleteLoader(true);
      await axiosInstance.delete(`projects/laydown?laydown_id=${deleteTarget?.id}`);
      await setLaydown();
    } catch (error) {
      console.error("Error deleting laydown:", error);
    } finally {
      setDeleteModalOpen(false);
      setDeleteLoader(false);
    }
  };

  const handleEditClick = (e, item) => {
    e.stopPropagation();
    setEditLaydownId(item);
    setEditLaydownName(item.name);
  };

  const handleEditCancel = () => {
    setEditLaydownId(null);
    setEditLaydownName("");
  };

  const handleEditSave = async () => {
    try {
      setEditLoader(true);
      await axiosInstance.post(`/projects/laydown`, {
        id: editLaydownId?.id,
        name: editLaydownName,
        project_id: Number(project_id),
        latitude: editLaydownId?.latitude,
        longitude: editLaydownId?.longitude
      });
      await setLaydown();
      setEditLaydownId(null);
    } catch (err) {
      console.error("Update failed:", err);
    } finally {
      setEditLoader(false);
    }
  };
  const sortedLaydowns = sortLaydownsByNameAsc(laydownList);

  return (
    <Box>
      <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography fontSize={20} fontWeight={600}>
          Laydown List
        </Typography>
        {laydownList.length ?
          <ToggleButtonComponent 
            data={["Scope view", "Laydown view"]} 
            active={active} 
            setActive={setActive} 
          /> : ''
        }
      </Box>

      <Box mb={2} sx={{ height: "calc(100vh - 260px)" , overflowY: "auto"}} >
        {sortedLaydowns?.map((item) => {
          const isActive = currentLaydown?.id === item.id;
          const isEditing = editLaydownId?.id === item.id;

          return (
            <Box
              key={item.id}
              onClick={() => handleLaydownClick(item)}
              p={1}
              mb={0.5}
              fontWeight={500}
              sx={{
                borderRadius: 1,
                backgroundColor: isActive ? "#e5e7eb" : "transparent",
                color: isActive ? "#000" : "inherit",
                cursor: isEditMode ? "not-allowed" : "pointer",
                opacity: isEditMode ? 0.6 : 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                "&:hover": {
                  backgroundColor: isEditMode ? (isActive ? "#e5e7eb" : "transparent") : "#e5e7eb",
                },
              }}
            >

              {isEditing ? (
                <TextField
                  size="small"
                  variant="standard"
                  value={editLaydownName}
                  onChange={(e) => setEditLaydownName(e.target.value)}
                  fullWidth
                  sx={{ mr: 2 }}
                />
              ) : (
                <span>{item?.name}</span>
              )}

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {isActive && (
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      backgroundColor: "green",
                      borderRadius: "50%",
                    }}
                  />
                )}

                {active === "Laydown view" &&
                  (isEditing ? (
                    <>
                      <IconButton
                        size="small"
                        onClick={handleEditSave}
                        disabled={editLoader || isReadOnly()}
                        color="success"
                      >
                        {editLoader ? <CircularProgress size={16} /> : <Check fontSize="small" />}
                      </IconButton>
                      <IconButton size="small" onClick={handleEditCancel} color="error">
                        <Close fontSize="small" />
                      </IconButton>
                    </>
                  ) : (
                    <>
                      <IconButton size="small" disabled={isReadOnly()} onClick={(e) => handleEditClick(e, item)}>
                        <Pencil size={18} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => handleDeleteClick(e, item)}
                        sx={{ color: "red" }}
                        disabled={isReadOnly()}
                      >
                        <DeleteIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </>
                  ))}
              </Box>
            </Box>
          );
        })}
      </Box>

     
      <Dialog open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are deleting the laydown with some current quantities and/or damaged quantities reported.
            This deletion will modify the initial delivery records accordingly.
            You may download the inventory log for your reference if required.
            <br />
            <br />
            Are you sure you want to delete <strong>{deleteTarget?.name}</strong>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteModalOpen(false)} disabled={deleteLoader}>
            Cancel
          </Button>
          <Button
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleteLoader}
            startIcon={deleteLoader && <CircularProgress size={16} />}
          >
            {deleteLoader ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LaydownList;
